// End gateways (DIM-3). PURE (ENG-1).
//
// The dragon fight left one thing unfinished: the way OUT. `end_fight.ts` raises the
// exit portal that sends you home to the overworld, but the outer islands — where
// the end cities, the chorus forests and the elytra are — sat 640+ blocks past a
// void gap with no way to cross it.
//
// A slain dragon fixes that: it lights one gateway of a fixed ring floating over the
// main island. Step in and you are thrown out along that gateway's own bearing, past
// the gap, onto the shards — and a RETURN gateway is raised over your landing so the
// trip is not one-way. Each subsequent dragon lights the next gateway in the ring.
//
// The whole ring is derived from config alone (no seed, no chunk state), so the
// generator, the host and a reloaded world all agree on where the gateways are — the
// same trick `endPillars` plays for the crystal towers.
import { END } from '../config';
import { STATE_AIR } from '../engine/section';

export type Cell = readonly [number, number, number];

/** The block accessor a gateway reads and writes (a live World satisfies it). */
export interface GatewayAccess {
  get(x: number, y: number, z: number): number;
  set(x: number, y: number, z: number, state: number): void;
  isSolid(x: number, y: number, z: number): boolean;
}

/** Block-state ids a gateway is built from. */
export interface GatewayIds {
  bedrock: number;
  endGateway: number;
  /** End stone for the landing pad; omit and a void landing gets no pad. */
  endStone?: number;
}

/**
 * The ring of gateway cells over the main island, evenly spaced on a circle at a
 * fixed height. Derived from config alone so every caller agrees.
 */
export function gatewayRing(): Cell[] {
  const out: Cell[] = [];
  for (let i = 0; i < END.GATEWAY_COUNT; i++) {
    const a = (i / END.GATEWAY_COUNT) * Math.PI * 2;
    out.push([
      Math.round(Math.cos(a) * END.GATEWAY_RADIUS),
      END.GATEWAY_Y,
      Math.round(Math.sin(a) * END.GATEWAY_RADIUS),
    ]);
  }
  return out;
}

/**
 * A gateway's bedrock nest: the 3×3×3 cube around it, minus the centre (which holds
 * the gateway itself) and minus its six FACE neighbours — so the frame is edges and
 * corners only and you can fly into it from any direction. Leaving the faces open is
 * a deliberate simplification of Minecraft's lumpier bedrock ball; the effect that
 * matters (a bedrock cage you cannot mine, with the portal visible inside) is the
 * same, and it removes the need to orient every gateway toward the island.
 */
export function gatewayPlan(cell: Cell): { gateway: Cell; bedrock: Cell[] } {
  const [cx, cy, cz] = cell;
  const bedrock: Cell[] = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        // Manhattan distance 0 = the gateway cell, 1 = an open face; 2 and 3 are
        // the edges and corners that make up the cage.
        if (Math.abs(dx) + Math.abs(dy) + Math.abs(dz) < 2) continue;
        bedrock.push([cx + dx, cy + dy, cz + dz]);
      }
    }
  }
  return { gateway: cell, bedrock };
}

/** Build a gateway (cage + portal cell) at `cell`. */
export function buildGateway(access: GatewayAccess, ids: GatewayIds, cell: Cell): Cell {
  const plan = gatewayPlan(cell);
  for (const [x, y, z] of plan.bedrock) access.set(x, y, z, ids.bedrock);
  // The six open faces are cleared too, so a gateway raised inside a chorus stalk or
  // a stray shard still has a mouth.
  for (const [dx, dy, dz] of [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]] as const) {
    access.set(cell[0] + dx, cell[1] + dy, cell[2] + dz, STATE_AIR);
  }
  access.set(cell[0], cell[1], cell[2], ids.endGateway);
  return cell;
}

/**
 * The next ring gateway to light: the first that is not already one. Null once the
 * whole ring is lit — the twentieth dragon is the last to leave a new door.
 */
export function nextRingGateway(access: GatewayAccess, endGatewayState: number): Cell | null {
  for (const c of gatewayRing()) {
    if (access.get(c[0], c[1], c[2]) !== endGatewayState) return c;
  }
  return null;
}

/**
 * Is a gateway one of the main-island ring (outbound), or a return gateway raised
 * out among the shards? The void gap is wide enough that the radius alone decides,
 * with no extra state to save.
 */
export function isRingGateway(x: number, z: number): boolean {
  return Math.hypot(x, z) < END.OUTER_START;
}

/** The NOMINAL column an outbound gateway aims at: its own bearing, past the gap.
 *  Where it actually lands is `gatewayIslandColumn`, which hunts for real ground
 *  around this point — the shards are scattered, so a bearing alone usually points
 *  at open void. */
export function gatewayExitColumn(cell: Cell): [number, number] {
  const [x, , z] = cell;
  const a = Math.atan2(z, x);
  return [Math.round(Math.cos(a) * END.GATEWAY_EXIT_RADIUS), Math.round(Math.sin(a) * END.GATEWAY_EXIT_RADIUS)];
}

/**
 * The column a gateway really throws you to: a SPECIFIC island rather than a
 * bearing.
 *
 * Aiming at a bearing and a fixed radius meant most gateways pointed at nothing —
 * the outer band is scattered shards, not a shell — so most trips ended on the
 * synthetic end-stone pad `arriveAtOuterIsland` lays over the void, and the end
 * cities out there were reached only by luck. This searches around the nominal
 * column for standable ground: outward and inward along the bearing first (the
 * shard band runs radially), then a short fan to either side.
 *
 * The search box is deliberately bounded well inside the ≈220-block spacing
 * between adjacent gateways' nominal columns, so no two gateways can claim the
 * same shard — which is the other half of "a specific island". The scan order is
 * fixed, so the same seed always sends the same gateway to the same place with no
 * saved state (the trick the whole ring is built on).
 */
export function gatewayIslandColumn(access: GatewayAccess, cell: Cell): { x: number; z: number; onIsland: boolean } {
  const [nx, nz] = gatewayExitColumn(cell);
  const a = Math.atan2(cell[2], cell[0]);
  // Unit vectors along the bearing and across it.
  const rx = Math.cos(a), rz = Math.sin(a);
  const px = -rz, pz = rx;
  const { GATEWAY_ISLAND_RADIAL: R, GATEWAY_ISLAND_LATERAL: L, GATEWAY_ISLAND_STEP: S } = END;
  for (let dr = 0; dr <= R; dr += S) {
    for (const rs of dr === 0 ? [0] : [1, -1]) {
      for (let dl = 0; dl <= L; dl += S) {
        for (const ls of dl === 0 ? [0] : [1, -1]) {
          const x = Math.round(nx + rx * dr * rs + px * dl * ls);
          const z = Math.round(nz + rz * dr * rs + pz * dl * ls);
          if (landingY(access, x, z) !== null) return { x, z, onIsland: true };
        }
      }
    }
  }
  return { x: nx, z: nz, onIsland: false };
}

/**
 * A standable landing in a column (feet Y), scanning down through the shard band, or
 * null when the column is open void.
 */
export function landingY(access: GatewayAccess, x: number, z: number): number | null {
  for (let y = END.OUTER_MAX_Y; y >= END.OUTER_MIN_Y; y--) {
    if (access.isSolid(x, y, z) && !access.isSolid(x, y + 1, z) && !access.isSolid(x, y + 2, z)) return y + 1;
  }
  return null;
}

/**
 * Land a traveller at a column out among the shards: stand them on the island if one
 * is there, else lay a small end-stone pad over the void so a gateway never drops
 * anyone into nothing. Either way a return gateway is raised overhead.
 */
export function arriveAtOuterIsland(
  access: GatewayAccess,
  ids: GatewayIds,
  x: number,
  z: number,
): { arrival: [number, number, number]; gateway: Cell; padded: boolean } {
  let y = landingY(access, x, z);
  const padded = y === null;
  if (y === null) {
    const r = END.GATEWAY_PAD_RADIUS;
    if (ids.endStone !== undefined) {
      for (let dz = -r; dz <= r; dz++) {
        for (let dx = -r; dx <= r; dx++) {
          access.set(x + dx, END.GATEWAY_PAD_Y, z + dz, ids.endStone);
          for (let dy = 1; dy <= END.GATEWAY_PAD_HEADROOM; dy++) access.set(x + dx, END.GATEWAY_PAD_Y + dy, z + dz, STATE_AIR);
        }
      }
    }
    y = END.GATEWAY_PAD_Y + 1;
  }
  const gateway = buildGateway(access, ids, [x, y + END.GATEWAY_RETURN_HEIGHT, z]);
  return { arrival: [x + 0.5, y, z + 0.5], gateway, padded };
}
